import { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { relTime } from "@/lib/time";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, PieChart, Pie, Cell, CartesianGrid } from "recharts";
import { ShieldAlert, ChevronRight, Truck, Leaf, Radio, ArrowUpRight, ArrowDownRight, Minus, Boxes } from "lucide-react";
import { Link } from "react-router-dom";

const MIX_COLORS = [
  "#1A362D", "#C85A3C", "#557C55", "#D4A24C", "#2C6E49", "#7B5E3B",
  "#4A6FA5", "#8C4A6B", "#4A4D4A", "#9DB17C", "#B8735A", "#3E7C7C",
];

// Month helpers for "this month vs last month" stream comparison.
const monthRange = (offset = 0) => {
  const n = new Date();
  const s = new Date(n.getFullYear(), n.getMonth() + offset, 1);
  const e = new Date(n.getFullYear(), n.getMonth() + offset + 1, 0);
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(s), end: iso(e), label: s.toLocaleString(undefined, { month: "short", year: "numeric" }) };
};

const STREAM_COLOR = (wt) =>
  wt === "recyclable" ? "var(--forest)" :
  wt === "hazardous" ? "var(--terracotta)" :
  wt === "organic" ? "#7B9E6B" : "#5B6663";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [licenses, setLicenses] = useState([]);
  const [recent, setRecent] = useState([]);
  const [activity, setActivity] = useState([]);
  const [clientMap, setClientMap] = useState({});
  const [streamsThis, setStreamsThis] = useState(null);
  const [streamsLast, setStreamsLast] = useState(null);

  useEffect(() => {
    api.get("/analytics/summary").then((r) => setSummary(r.data));
    api.get("/licenses").then((r) => setLicenses(r.data));
    // Current + previous month stream breakdowns for the "This month vs last month" card.
    const thisM = monthRange(0), lastM = monthRange(-1);
    api.get(`/analytics/summary?start=${thisM.start}&end=${thisM.end}`)
      .then((r) => setStreamsThis({ ...r.data, label: thisM.label }))
      .catch(() => setStreamsThis(null));
    api.get(`/analytics/summary?start=${lastM.start}&end=${lastM.end}`)
      .then((r) => setStreamsLast({ ...r.data, label: lastM.label }))
      .catch(() => setStreamsLast(null));
    api.get("/collections").then((r) => {
      setRecent(r.data.slice(0, 6));
      // Live-activity feed: sort by created_at desc (most recently logged), take 10
      const sorted = [...r.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setActivity(sorted.slice(0, 10));
    });
    api.get("/clients").then((r) => {
      const map = {};
      r.data.forEach((c) => (map[c.id] = c.name));
      setClientMap(map);
    });

    // Poll the activity feed every 30 s so admins see stop completions land live.
    const t = setInterval(() => {
      api.get("/collections").then((r) => {
        const sorted = [...r.data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setActivity(sorted.slice(0, 10));
      }).catch(() => {});
    }, 30_000);
    return () => clearInterval(t);
  }, []);

  const expiring = licenses.filter((l) => l.status === "expiring_soon" || l.status === "expired").slice(0, 4);

  return (
    <div>
      <PageHeader
        eyebrow={user?.role === "admin" ? "HBT Team" : "Client Portal"}
        title={`Good day, ${user?.name?.split(" ")[0] || ""}.`}
        subtitle={user?.role === "admin"
          ? "Company-wide waste intelligence across all clients."
          : "Your live sustainability dashboard, updated in real time."}
      />

      <div className="px-8 lg:px-12 py-8 space-y-8">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 stagger">
          <KpiCard testid="kpi-total-waste" label="Total waste" value={summary ? summary.total_kg.toLocaleString() : "—"} unit="kg" />
          <KpiCard testid="kpi-diversion" label="Diversion" value={summary ? `${summary.diversion_rate}%` : "—"} accent="var(--forest)" />
          <KpiCard testid="kpi-recycling" label="Recycling" value={summary ? `${summary.recycling_rate}%` : "—"} accent="var(--forest)" />
          <KpiCard testid="kpi-co2" label="CO₂e saved" value={summary ? summary.co2_saved_kg.toLocaleString() : "—"} unit="kg" />
          <KpiCard testid="kpi-esg" label="ESG score" value={summary ? `${summary.esg_score}` : "—"} unit="/100" accent="var(--terracotta)" />
          <KpiCard testid="kpi-collections" label="Collections" value={summary?.collection_count ?? "—"} hint="all time" />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="hbt-card p-6 lg:col-span-2">
            <div className="flex items-center justify-between mb-6">
              <div>
                <div className="hbt-label">Monthly trend</div>
                <div className="font-display text-xl font-semibold mt-1">Waste diverted vs landfilled</div>
              </div>
            </div>
            <div className="h-[280px]" data-testid="trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={summary?.trend || []} margin={{ top: 10, right: 12, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="0" stroke="rgba(10,10,10,0.06)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4A4D4A" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#4A4D4A" }} axisLine={false} tickLine={false} />
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid rgba(10,10,10,0.15)", fontSize: 12 }} />
                  <Bar dataKey="diverted" name="Diverted" fill="#1A362D" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="landfill" name="Landfill" fill="#C85A17" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="hbt-card p-6">
            <div className="hbt-label">Waste mix</div>
            <div className="font-display text-xl font-semibold mt-1 mb-4">By waste stream</div>
            <div className="h-[220px]" data-testid="type-chart">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={summary?.by_stream || []} dataKey="kg" nameKey="name" cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {(summary?.by_stream || []).map((entry, i) => (
                      <Cell key={entry.key} fill={MIX_COLORS[i % MIX_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid rgba(10,10,10,0.15)", fontSize: 12 }} formatter={(v) => `${v.toFixed(1)} kg`} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="space-y-2 mt-2 max-h-48 overflow-y-auto pr-1">
              {(summary?.by_stream || []).map((row, i) => (
                <div key={row.key} className="flex items-center justify-between gap-2 text-xs" data-testid={`dash-mix-${row.key}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: MIX_COLORS[i % MIX_COLORS.length] }} />
                    <span className="truncate" title={row.name}>{row.name}</span>
                  </div>
                  <span className="font-mono-num shrink-0">{row.kg.toFixed(1)} kg</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Waste streams: this month vs last month */}
        <StreamCompareCard thisMonth={streamsThis} lastMonth={streamsLast} />

        {/* Two column: recent + alerts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="hbt-card lg:col-span-2 overflow-hidden">
            <div className="p-6 flex items-center justify-between">
              <div>
                <div className="hbt-label">Recent activity</div>
                <div className="font-display text-xl font-semibold mt-1">Latest collections</div>
              </div>
              <Link to="/app/collections" className="text-xs text-neutral-500 hover:text-black flex items-center gap-1" data-testid="see-all-collections">
                View all <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
            <table className="hbt-table">
              <thead>
                <tr>
                  <th>Date</th>
                  {user?.role === "admin" && <th>Client</th>}
                  <th>Waste stream</th>
                  <th>Method</th>
                  <th className="num">Weight</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((c) => (
                  <tr key={c.id} data-testid={`recent-row-${c.id}`}>
                    <td>{c.collection_date}</td>
                    {user?.role === "admin" && <td>{clientMap[c.client_id] || "—"}</td>}
                    <td><span className={`pill ${c.waste_type === "recyclable" ? "forest" : c.waste_type === "hazardous" ? "error" : c.waste_type === "organic" ? "success" : ""}`} title={c.waste_type}>{c.waste_stream_name || c.waste_type}</span></td>
                    <td className="capitalize text-neutral-600">{c.disposal_method}</td>
                    <td className="num font-mono-num">{c.weight_kg.toFixed(1)} kg</td>
                  </tr>
                ))}
                {recent.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-neutral-400 py-10">No collections recorded yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="hbt-card p-6">
            <div className="flex items-center justify-between mb-2">
              <div className="hbt-label">Attention</div>
              <ShieldAlert className="w-4 h-4" style={{ color: "var(--terracotta)" }} />
            </div>
            <div className="font-display text-xl font-semibold">Licenses expiring</div>
            <div className="mt-5 space-y-3">
              {expiring.length === 0 && <div className="text-sm text-neutral-500">All licenses current. Nice.</div>}
              {expiring.map((l) => (
                <div key={l.id} className="border-b last:border-0 pb-3 last:pb-0" style={{ borderColor: "var(--border)" }} data-testid={`license-alert-${l.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold">{l.name}</div>
                    <span className={`pill ${l.status === "expired" ? "error" : "warn"}`}>
                      {l.status === "expired" ? "Expired" : `${l.days_to_expiry}d left`}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">{l.issuing_authority}</div>
                </div>
              ))}
            </div>
            <Link to="/app/licenses" className="btn-outline mt-6 inline-flex items-center gap-2 w-full justify-center" data-testid="view-licenses-btn">
              Manage licenses <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Live activity feed */}
        <div className="hbt-card overflow-hidden" data-testid="live-activity-panel">
          <div className="p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: "var(--forest)" }}></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: "var(--forest)" }}></span>
              </span>
              <div>
                <div className="hbt-label flex items-center gap-1.5">
                  <Radio className="w-3 h-3" /> Live
                </div>
                <div className="font-display text-xl font-semibold mt-1">Activity feed</div>
              </div>
            </div>
            <div className="text-xs text-neutral-500">Auto-refresh · 30s</div>
          </div>
          <div className="border-t" style={{ borderColor: "var(--border)" }}>
            {activity.length === 0 && (
              <div className="text-center text-neutral-400 py-10 text-sm">No activity yet. Logged stops will land here in real time.</div>
            )}
            <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
              {activity.map((a) => (
                <li key={a.id} className="px-6 py-3 flex items-center gap-4 hover:bg-black/[0.02] transition-colors" data-testid={`activity-row-${a.id}`}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(26,54,45,0.08)" }}>
                    <Truck className="w-4 h-4" style={{ color: "var(--forest)" }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">
                      {clientMap[a.client_id] || (a.client_id ? `#${a.client_id.slice(0, 6)}` : "Client")} · <span className="capitalize text-neutral-600">{a.waste_type}</span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5 capitalize">{a.disposal_method} · {a.collection_date}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono-num text-sm font-semibold">{Number(a.weight_kg || 0).toFixed(1)} kg</div>
                    <div className="text-[11px] text-neutral-400 mt-0.5">{relTime(a.created_at)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Quick actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {user?.role === "admin" ? (
            <Link to="/app/collections" className="hbt-card p-6 flex items-center gap-4 hover:border-black/30" data-testid="quick-log-collection">
              <Truck className="w-6 h-6" style={{ color: "var(--forest)" }} />
              <div>
                <div className="font-semibold">Log a collection</div>
                <div className="text-xs text-neutral-500 mt-0.5">Record a new pickup</div>
              </div>
            </Link>
          ) : (
            <Link to="/app/pickups" className="hbt-card p-6 flex items-center gap-4 hover:border-black/30" data-testid="quick-request-pickup">
              <Truck className="w-6 h-6" style={{ color: "var(--forest)" }} />
              <div>
                <div className="font-semibold">Request a pickup</div>
                <div className="text-xs text-neutral-500 mt-0.5">Schedule collection with HBT</div>
              </div>
            </Link>
          )}
          <Link to="/app/reports" className="hbt-card p-6 flex items-center gap-4 hover:border-black/30" data-testid="quick-monthly-report">
            <Leaf className="w-6 h-6" style={{ color: "var(--forest)" }} />
            <div>
              <div className="font-semibold">Download monthly report</div>
              <div className="text-xs text-neutral-500 mt-0.5">PDF with AI insights</div>
            </div>
          </Link>
          <Link to="/app/sustainability" className="hbt-card p-6 flex items-center gap-4 hover:border-black/30" data-testid="quick-esg-insights">
            <Leaf className="w-6 h-6" style={{ color: "var(--terracotta)" }} />
            <div>
              <div className="font-semibold">View ESG insights</div>
              <div className="text-xs text-neutral-500 mt-0.5">AI recommendations</div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------
// "By waste stream — this month vs last month" card.
// Merges by_stream rows from both month summaries so every stream the
// client produced in either month appears with a side-by-side delta.
// ------------------------------------------------------------------
function StreamCompareCard({ thisMonth, lastMonth }) {
  const loading = !thisMonth || !lastMonth;
  const rows = (() => {
    if (loading) return [];
    const map = {};
    (thisMonth.by_stream || []).forEach((r) => {
      map[r.key] = { key: r.key, name: r.name, unit: r.unit || "kg", waste_type: r.waste_type, this_kg: r.kg || 0, last_kg: 0 };
    });
    (lastMonth.by_stream || []).forEach((r) => {
      if (!map[r.key]) map[r.key] = { key: r.key, name: r.name, unit: r.unit || "kg", waste_type: r.waste_type, this_kg: 0, last_kg: r.kg || 0 };
      else map[r.key].last_kg = r.kg || 0;
    });
    return Object.values(map).sort((a, b) => (b.this_kg + b.last_kg) - (a.this_kg + a.last_kg));
  })();
  const maxKg = Math.max(1, ...rows.map((r) => Math.max(r.this_kg, r.last_kg)));

  return (
    <div className="hbt-card p-6" data-testid="streams-month-compare">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <div className="hbt-label flex items-center gap-1.5"><Boxes className="w-3 h-3" /> Waste streams</div>
          <div className="font-display text-xl font-semibold mt-1">This month vs last month</div>
          <div className="text-xs text-neutral-500 mt-1">
            Every specific stream — cardboard, plastic, glass, organics and more — side by side.
          </div>
        </div>
        {!loading && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">{thisMonth.label} · {lastMonth.label}</div>
            <div className="text-xs text-neutral-500 mt-1 font-mono-num">{rows.length} stream{rows.length === 1 ? "" : "s"}</div>
          </div>
        )}
      </div>

      {loading && <div className="text-sm text-neutral-400 py-6">Loading stream breakdown…</div>}

      {!loading && rows.length === 0 && (
        <div className="text-sm text-neutral-500 py-8 text-center">
          No waste streams captured yet for this or last month. Once collections are logged you'll see each stream here.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-4">
          {/* Header row */}
          <div className="grid grid-cols-[minmax(140px,1.4fr)_2fr_110px_110px_90px] gap-3 items-center text-[10px] uppercase tracking-widest text-neutral-500 pb-2 border-b" style={{ borderColor: "var(--border)" }}>
            <div>Stream</div>
            <div>Volume (this vs last)</div>
            <div className="text-right">This month</div>
            <div className="text-right">Last month</div>
            <div className="text-right">Δ</div>
          </div>

          {rows.map((row) => {
            const diff = row.this_kg - row.last_kg;
            const pct = row.last_kg > 0 ? (diff / row.last_kg) * 100 : (row.this_kg > 0 ? 100 : 0);
            const tone = diff > 0 ? "positive" : diff < 0 ? "negative" : "neutral";
            const color = STREAM_COLOR(row.waste_type);
            const thisPct = Math.max(2, Math.round((row.this_kg / maxKg) * 100));
            const lastPct = Math.max(2, Math.round((row.last_kg / maxKg) * 100));
            return (
              <div key={row.key} className="grid grid-cols-[minmax(140px,1.4fr)_2fr_110px_110px_90px] gap-3 items-center" data-testid={`stream-compare-${row.key}`}>
                <div className="text-sm font-medium truncate" title={row.name}>{row.name}</div>
                <div className="space-y-1.5">
                  <div className="h-2 rounded-full bg-neutral-100 overflow-hidden" title={`This month ${row.this_kg.toFixed(1)} ${row.unit}`}>
                    <div className="h-full transition-all" style={{ width: `${thisPct}%`, background: color }} />
                  </div>
                  <div className="h-2 rounded-full bg-neutral-100 overflow-hidden" title={`Last month ${row.last_kg.toFixed(1)} ${row.unit}`}>
                    <div className="h-full transition-all" style={{ width: `${lastPct}%`, background: color, opacity: 0.4 }} />
                  </div>
                </div>
                <div className="text-right font-mono-num text-sm font-semibold" data-testid={`stream-this-${row.key}`}>
                  {row.this_kg.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  <span className="text-neutral-400 font-normal ml-1">{row.unit}</span>
                </div>
                <div className="text-right font-mono-num text-sm text-neutral-500" data-testid={`stream-last-${row.key}`}>
                  {row.last_kg.toLocaleString(undefined, { maximumFractionDigits: 1 })}
                  <span className="text-neutral-400 ml-1">{row.unit}</span>
                </div>
                <div className="text-right text-xs font-mono-num flex items-center justify-end gap-1" style={{ color: tone === "positive" ? "var(--forest)" : tone === "negative" ? "var(--terracotta)" : "var(--muted-ink)" }}>
                  {tone === "positive" && <ArrowUpRight className="w-3 h-3" />}
                  {tone === "negative" && <ArrowDownRight className="w-3 h-3" />}
                  {tone === "neutral" && <Minus className="w-3 h-3" />}
                  {row.last_kg === 0 && row.this_kg > 0 ? "new" : `${pct > 0 ? "+" : ""}${pct.toFixed(0)}%`}
                </div>
              </div>
            );
          })}

          {/* Legend */}
          <div className="flex items-center gap-4 pt-3 mt-2 border-t text-[11px] text-neutral-500" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-5 h-1.5 rounded-full" style={{ background: "var(--forest)" }} /> This month
            </div>
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-5 h-1.5 rounded-full" style={{ background: "var(--forest)", opacity: 0.4 }} /> Last month
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
